import { Link } from "react-router-dom";
import {
  ACHIEVEMENTS,
  getUnlockedMap,
  getStreak,
} from "@/lib/achievements";
import { todayString } from "@/lib/gameEngine";
import {
  ArrowLeft,
  Flag,
  Skull,
  Crosshair,
  Coins,
  Zap,
  Flame,
  Lock,
  Calendar,
} from "lucide-react";

function iconFor(key, className) {
  switch (key) {
    case "flag":
      return <Flag className={className} />;
    case "skull":
      return <Skull className={className} />;
    case "crosshair":
      return <Crosshair className={className} />;
    case "coins":
      return <Coins className={className} />;
    case "zap":
      return <Zap className={className} />;
    case "flame":
      return <Flame className={className} />;
    case "flame-double":
      return (
        <span className={`inline-flex ${className}`}>
          <Flame className="w-4 h-4" />
          <Flame className="w-4 h-4 -ml-1" />
        </span>
      );
    default:
      return <Flag className={className} />;
  }
}

export default function AchievementsScreen() {
  const unlocked = getUnlockedMap();
  const streak = getStreak(todayString());
  const totalUnlocked = Object.keys(unlocked).length;

  return (
    <div
      data-testid="achievements-screen"
      className="relative min-h-screen w-full bg-black px-4 py-8 sm:py-12 scanlines"
    >
      <div className="max-w-2xl mx-auto">
        <BackLink />
        <ScreenHeader totalUnlocked={totalUnlocked} total={ACHIEVEMENTS.length} />
        <StreakCard streak={streak} />
        <AchievementsGrid unlocked={unlocked} />
        <PlayCta />
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/"
      data-testid="achievements-back-button"
      className="inline-flex items-center gap-2 font-hud text-white/70 hover:text-white text-base mb-6"
    >
      <ArrowLeft className="w-4 h-4" /> BACK TO TITLE
    </Link>
  );
}

function ScreenHeader({ totalUnlocked, total }) {
  return (
    <div className="text-center mb-6">
      <p className="font-hud text-[var(--street-yellow)] tracking-[0.4em] text-sm">
        // BADGES
      </p>
      <h1 className="font-display text-6xl sm:text-7xl text-white tracking-tight mt-1">
        ACHIEVEMENTS
      </h1>
      <p className="font-hud text-white/60 text-base mt-2">
        {totalUnlocked} / {total} UNLOCKED
      </p>
    </div>
  );
}

function StreakCard({ streak }) {
  const hasStreak = streak > 0;
  const flameFilter = hasStreak
    ? "drop-shadow(0 0 12px rgba(255,0,60,0.7))"
    : "none";
  return (
    <div
      className="mb-6 border-2 border-[var(--street-yellow)]/50 bg-black/60 rounded-xl p-4 flex items-center justify-between"
      data-testid="daily-streak-card"
    >
      <div className="flex items-center gap-3">
        <Calendar className="w-7 h-7 text-[var(--street-yellow)]" />
        <div>
          <div className="font-hud text-white/70 text-sm tracking-[0.25em]">
            DAILY STREAK
          </div>
          <div className="font-display text-3xl text-white">
            {streak} DAY{streak === 1 ? "" : "S"}
          </div>
        </div>
      </div>
      <Flame
        className={`w-10 h-10 ${
          hasStreak ? "text-[var(--neon-red)]" : "text-white/20"
        }`}
        style={{ filter: flameFilter }}
      />
    </div>
  );
}

function AchievementsGrid({ unlocked }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {ACHIEVEMENTS.map((a) => (
        <AchievementCard
          key={a.id}
          achievement={a}
          unlockedAt={unlocked[a.id]}
        />
      ))}
    </div>
  );
}

function AchievementCard({ achievement, unlockedAt }) {
  const isUnlocked = !!unlockedAt;
  return (
    <div
      data-testid={`achievement-card-${achievement.id}`}
      className={`border-2 rounded-lg p-4 flex items-start gap-3 ${
        isUnlocked ? "bg-black/70" : "bg-black/30"
      }`}
      style={{
        borderColor: isUnlocked
          ? `${achievement.color}99`
          : "rgba(255,255,255,0.12)",
        boxShadow: isUnlocked ? `0 0 14px ${achievement.color}33` : "none",
        opacity: isUnlocked ? 1 : 0.55,
      }}
    >
      <AchievementIcon achievement={achievement} isUnlocked={isUnlocked} />
      <div className="min-w-0">
        <div
          className="font-display text-xl tracking-tight"
          style={{
            color: isUnlocked ? achievement.color : "rgba(255,255,255,0.7)",
          }}
        >
          {achievement.label}
        </div>
        <div className="font-hud text-white/70 text-base leading-tight">
          {achievement.desc}
        </div>
        {isUnlocked && (
          <div className="font-hud text-white/40 text-xs mt-1 tracking-wider">
            UNLOCKED {new Date(unlockedAt).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}

function AchievementIcon({ achievement, isUnlocked }) {
  return (
    <div
      className="w-11 h-11 rounded-md grid place-items-center shrink-0"
      style={{
        background: isUnlocked
          ? `${achievement.color}22`
          : "rgba(255,255,255,0.05)",
        color: isUnlocked ? achievement.color : "rgba(255,255,255,0.4)",
      }}
    >
      {isUnlocked ? (
        iconFor(achievement.iconKey, "w-5 h-5")
      ) : (
        <Lock className="w-5 h-5" />
      )}
    </div>
  );
}

function PlayCta() {
  return (
    <div className="mt-8 text-center">
      <Link
        to="/play"
        data-testid="achievements-play-button"
        className="inline-flex items-center gap-2 bg-[var(--neon-red)] hover:bg-red-600 text-white font-body font-bold uppercase tracking-[0.18em] px-6 py-3 rounded-md transition-all"
      >
        CHASE MORE
      </Link>
    </div>
  );
}
